import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter } from '@angular/core';

import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { faTrash, faEye, faFile, faSpinner, faCircleInfo, faSearch, faTimes, faDownload, faCodeCompare, faRedo } from '@fortawesome/free-solid-svg-icons';

import { ItemListComponent, ListConfig, ListItem } from '../item-list/item-list.component';
import { ModalViewLogComponent } from '../modal-view-log/modal-view-log.component';
import { ModalDeleteConfirmComponent } from '../modal-delete-confirm/modal-delete-confirm.component';
import { ModalExecutionDetailsComponent } from '../modal-execution-details/modal-execution-details.component';
import { ResultsService } from '../../services/results.service';
import { AlertService } from '../alert-service/alert-service';
import { DateFormatterService } from '../../services/date-formatter.service';
import { TenantService } from '../../services/tenant.service';
import { normalizeForSearch } from '../../utils/text-search.util';

interface Result {
  id: string;
  interaction_id: string;
  result_number?: number;
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'PARTIALLY_COMPLETED';
  archive_status?: Record<string, { status: 'pending' | 'done' | 'blocked' | 'failed'; error: string | null }> | null;
  execution_config?: {
    delfos_server_config_id?: string | null;
    delfos_llm_config_id?: string | null;
    di2winocr_server_config_id?: string | null;
    di2winocr_features_config_id?: string | null;
    clear_delfos_cache: boolean;
    clear_di2winocr_cache: boolean;
  };
  progress?: {
    current: number;
    total: number;
    percentage: number;
  };
  config_summary?: {
    model_name?: string | null;
    delfos_server?: string | null;
    delfos_llm?: string | null;
    di2winocr_server?: string | null;
    di2winocr_features?: string | null;
    di2winocr_features_customized?: boolean;
    use_multimodal?: boolean;
    ai_model_language?: string;
    clear_delfos_cache?: boolean;
    clear_di2winocr_cache?: boolean;
  };
  logs_path: string;
  output_path: string;
  created_at: string;
  updated_at: string;
}

@Component({
  selector: 'app-interaction-results',
  standalone: true,
  imports: [
    TranslateModule,
    FontAwesomeModule,
    CommonModule,
    FormsModule,
    ItemListComponent,
    ModalViewLogComponent,
    ModalDeleteConfirmComponent,
    ModalExecutionDetailsComponent
],
  templateUrl: './interaction-results.component.html',
  styleUrls: ['./interaction-results.component.scss']
})
export class InteractionResultsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() interactionId: string = '';
  @Input() loadingResults = false;
  @Input() results: Result[] = [];
  @Input() isTabActive = false;
  @Output() refreshResults = new EventEmitter<void>();

  listItems: ListItem[] = [];
  listConfig!: ListConfig;

  showLogModal = false;
  selectedResultId = '';
  
  showDeleteModal = false;
  resultToDelete: ListItem | null = null;
  showExecutionDetailsModal = false;
  selectedResultForDetails: ListItem | null = null;

  faFile = faFile;
  faSpinner = faSpinner;
  faSearch = faSearch;
  faTimes = faTimes;
  faCodeCompare = faCodeCompare;
  faRedo = faRedo;

  compareSelection: Set<string> = new Set();

  searchText: string = '';

  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL_MS = 5000;

  constructor(
    private resultsService: ResultsService,
    private alert: AlertService,
    private translate: TranslateService,
    private router: Router,
    private dateFormatter: DateFormatterService,
    private tenantService: TenantService
  ) {}

  get canCompare(): boolean {
    return this.compareSelection.size >= 2;
  }

  onItemSelectionChanged(event: { item: ListItem; selected: boolean }): void {
    const id = event.item.id!.toString();
    if (event.selected) {
      this.compareSelection = new Set([...this.compareSelection, id]);
    } else {
      const next = new Set(this.compareSelection);
      next.delete(id);
      this.compareSelection = next;
    }
  }

  navigateToCompare(): void {
    if (!this.canCompare) return;
    this.router.navigate(
      this.tenantService.buildRoute(['/simulations', this.interactionId, 'compare']),
      { queryParams: { r: Array.from(this.compareSelection) } }
    );
  }

  ngOnInit(): void {
    this.initializeListConfig();
  }

  ngOnChanges(): void {
    if (this.results) {
      this.mapResultsToListItems();
      this.checkAndStartPolling();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private checkAndStartPolling(): void {
    const hasPendingResults = this.results.some(r => r.status === 'PENDING');

    if (hasPendingResults && this.isTabActive && !this.pollingInterval) {
      this.startPolling();
    } else if (!hasPendingResults || !this.isTabActive) {
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollingInterval) return;

    this.pollingInterval = setInterval(() => {
      this.refreshResults.emit();
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private initializeListConfig(): void {
    this.listConfig = {
      itemIcon: this.faFile,
      itemIconClass: 'result-icon',
      showMetadata: true,
      showType: true,
      typeLabels: {
        'PENDING': this.translate.instant('INTERACTIONS.RESULTS.STATUS.PENDING'),
        'COMPLETED': this.translate.instant('INTERACTIONS.RESULTS.STATUS.COMPLETED'),
        'FAILED': this.translate.instant('INTERACTIONS.RESULTS.STATUS.FAILED'),
        'CANCELLED': this.translate.instant('INTERACTIONS.RESULTS.STATUS.CANCELLED'),
        'PARTIALLY_COMPLETED': this.translate.instant('INTERACTIONS.RESULTS.STATUS.PARTIALLY_COMPLETED')
      },
      emptyMessage: 'INTERACTIONS.RESULTS.NO_RESULTS',
      emptyMessageFiltered: 'INTERACTIONS.RESULTS.NO_RESULTS',
      showConfigSummary: true,
      showSelectCheckboxes: true,
      checkboxVisible: (item: ListItem) => item['status'] === 'COMPLETED' || item['status'] === 'PARTIALLY_COMPLETED',
      actions: [
        {
          id: 'execution-details',
          label: 'RESULT.EXECUTION_DETAILS',
          icon: faCircleInfo,
          cssClass: 'action-execution-details'
        },
        {
          id: 'view-log',
          label: 'BUTTON.VIEW_LOG',
          icon: faEye,
          cssClass: 'action-view-log'
        },
        {
          id: 'download',
          label: 'BUTTON.DOWNLOAD_ALL_JSON',
          icon: faDownload,
          cssClass: 'action-download',
          visible: (item: ListItem) => item['status'] === 'COMPLETED' || item['status'] === 'PARTIALLY_COMPLETED'
        },
        {
          id: 'download-ocr',
          label: 'BUTTON.DOWNLOAD_OCR',
          icon: faDownload,
          cssClass: 'action-download-ocr',
          visible: (item: ListItem) =>
            item['status'] === 'COMPLETED' ||
            item['status'] === 'PARTIALLY_COMPLETED'
        },
        {
          id: 'retry-failed',
          label: 'BUTTON.RETRY_FAILED',
          icon: faRedo,
          cssClass: 'action-retry-failed',
          visible: (item: ListItem) => item['status'] === 'FAILED' || item['status'] === 'PARTIALLY_COMPLETED'
        },
        {
          id: 'cancel',
          label: 'BUTTON.CANCEL',
          icon: faTimes,
          cssClass: 'action-cancel',
          visible: (item: ListItem) => item['status'] === 'PENDING'
        },
        {
          id: 'delete',
          label: 'BUTTON.DELETE',
          icon: faTrash,
          cssClass: 'action-delete'
        },
      ]
    };
  }

  private mapResultsToListItems(): void {
    this.listItems = this.results.map(result => {
      const resultName = result.result_number 
        ? this.translate.instant('INTERACTIONS.RESULTS.RESULT_NAME', { number: result.result_number })
        : result.id;
      
      return {
        id: result.id,
        name: resultName,
        configSummary: result.config_summary || null,
        type: result.status,
        logs_path: result.logs_path,
        output_path: result.output_path,
        status: result.status,
        progress: result?.progress || { current: 0, total: 0, percentage: 0 },
        raw: result
      };
    });
  }

  get filteredListItems(): ListItem[] {
    if (!this.searchText) return this.listItems;
    const search = normalizeForSearch(this.searchText);
    return this.listItems.filter(item =>
      normalizeForSearch(item.name).includes(search) ||
      normalizeForSearch(item['status']).includes(search)
    );
  }

  clearSearch(): void {
    this.searchText = '';
  }

  private formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private formatTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  onItemClick(item: ListItem): void {
    if (item['status'] === 'COMPLETED' || item['status'] === 'PARTIALLY_COMPLETED') {
      this.router.navigate(this.tenantService.buildRoute(['/simulations', this.interactionId, 'results', item.id]));
    }
  }

  onActionClick(event: { action: string; item: ListItem; event: Event }): void {
    const { action, item } = event;

    switch (action) {
      case 'view-log':
        this.viewLog(item);
        break;
      case 'delete':
        this.openDeleteModal(item);
        break;
      case 'execution-details':
        this.openExecutionDetails(item);
        break;
      case 'download':
        this.downloadResult(item);
        break;
      case 'download-ocr':
        this.downloadOcr(item);
        break;
      case 'cancel':
        this.cancelExecution(item);
        break;
      case 'retry-failed':
        this.retryFailedArchives(item);
        break;
    }
  }

  private cancelExecution(item: ListItem): void {
    this.resultsService.cancelExecution(this.interactionId, item.id as string).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('INTERACTIONS.RESULTS.CANCEL_SUCCESS'));
        this.refreshResults.emit();
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('INTERACTIONS.RESULTS.CANCEL_ERROR'));
      }
    });
  }

  private retryFailedArchives(item: ListItem): void {
    this.resultsService.retryFailedArchives(this.interactionId, item.id as string).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('INTERACTIONS.RESULTS.RETRY_SUCCESS'));
        this.refreshResults.emit();
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('INTERACTIONS.RESULTS.RETRY_ERROR'));
      }
    });
  }

  private viewLog(item: ListItem): void {
    this.selectedResultId = item.id as string;
    this.showLogModal = true;
  }

  onCloseLogModal(): void {
    this.showLogModal = false;
    this.selectedResultId = '';
  }

  private openDeleteModal(item: ListItem): void {
    this.resultToDelete = item;
    this.showDeleteModal = true;
  }

  onCloseDeleteModal(): void {
    this.showDeleteModal = false;
    this.resultToDelete = null;
  }

  onConfirmDelete(): void {
    if (!this.resultToDelete) return;

    this.resultsService.deleteResult(this.interactionId, this.resultToDelete.id as string).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('INTERACTIONS.DELETE_SUCCESS'));
        this.showDeleteModal = false;
        this.resultToDelete = null;
        this.refreshResults.emit();
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('INTERACTIONS.DELETE_ERROR'));
        this.showDeleteModal = false;
        this.resultToDelete = null;
      }
    });
  }

  private openExecutionDetails(item: ListItem): void {
    this.selectedResultForDetails = item;
    this.showExecutionDetailsModal = true;
  }

  onCloseExecutionDetails(): void {
    this.showExecutionDetailsModal = false;
    this.selectedResultForDetails = null;
  }
  
  private downloadResult(item: ListItem): void {
    const zipName = item.name;
    this.resultsService.downloadAllResultsAsZip(this.interactionId, item.id as string, zipName)
      .catch(error => {
        this.alert.showError(error, this.translate.instant('ERROR.DOWNLOADING_JSON'));
      });
  }

  private downloadOcr(item: ListItem): void {
    const zipName = item.name;
    this.resultsService.downloadAllOcrAsZip(this.interactionId, item.id as string, zipName)
      .catch(error => {
        this.alert.showError(error, this.translate.instant('ERROR.DOWNLOADING_OCR'));
      });
  }
}